import { type AnyAction } from 'redux';
import { call, CallEffect, ForkEffect, put, PutEffect, takeLatest } from 'redux-saga/effects';

import { handleEmptyNullValues, handleSortingValues } from '@utilities';

import { CommercialPaymentActionType } from './enums';
import { approvePayment, createGstInvoice, fetchCustomerSlip, fetchGstInvoice, fetchPaymentSlip, getAllPayments, projectAutocomplete } from './api';

type CommercialPaymentSagaEffect = Generator<CallEffect<any> | PutEffect<any>>;
type CommercialPaymentSagaForEffect = Generator<ForkEffect<void>>;

const paymentsFiltersObject = (obj: Record<string, any>) => {
	const transformData = {
		filters: {
			...obj.filters,
			paymentStatus: !!obj.filters.paymentStatus?.length
				? obj.filters.paymentStatus.map(paymentStatus => paymentStatus.key)
				: [],
			leadIds: !!obj.filters.leadName?.length ? [obj?.filters?.leadName[0]?.leadId] : '',
			projectIds: !!obj.filters.projectName?.length ? [obj?.filters?.projectName[0]?.id] : ''
		}
	};
	delete transformData?.filters?.leadName;
	delete transformData?.filters?.projectName;
	return {
		...obj,
		filters: transformData.filters
	};
};

export function* fetchAllCommercialPayments(action: AnyAction): CommercialPaymentSagaEffect {
	const { paymentsFilter, errorCB } = action.payload;
	// let transformedFilters = handleSortingValues(paymentsFilter, SORT_COLUMN_KEYS);
	const transformedData = paymentsFiltersObject(paymentsFilter);
	const transformedFilters = handleEmptyNullValues(transformedData);

	try {
		const { data }: any = yield call(getAllPayments, transformedFilters);
		yield put({
			type: CommercialPaymentActionType.STORE_ALL_PAYMENTS,
			payload: data
		});
	} catch (error: unknown) {
		yield put({
			type: CommercialPaymentActionType.ERROR_ALL_PAYMENTS,
			payload: {
				error
			}
		});
		errorCB(error?.message);
	}
}

export function* fetchProjectAutocompleteEffect(action: AnyAction): CommercialPaymentSagaEffect {
	const { prefix, errorCB } = action.payload;
	try {
		const { data }: any = yield call(projectAutocomplete, prefix);

		const projectsList = data.map((projectItem: Record<string, any>) => {
			return {
				...projectItem,
				displayName: projectItem.name
			};
		});

		yield put({ type: CommercialPaymentActionType.STORE_PROJECT_AUTOCOMPLETE, payload: { data: projectsList } });
	} catch (error: unknown) {
		yield put({
			type: CommercialPaymentActionType.ERROR_FETCH_PROJECT_AUTOCOMPLETE,
			payload: {
				error
			}
		});
		errorCB(error?.message);
	}
}

export function* fetchCustomerSlipsEffect(action: AnyAction): CommercialPaymentSagaEffect {
	const { errorCB, docId } = action.payload;
	try {
		const { data }: any = yield call(fetchCustomerSlip, { documentIds: [docId] });

		yield put({ type: CommercialPaymentActionType.STORE_CUSTOMER_SLIPS, payload: { data } });
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* fetchPaymentSlipsEffect(action: AnyAction): CommercialPaymentSagaEffect {
	const { errorCB, paymentApprovalId, finalCB, successCB } = action.payload;

	try {
		const { data }: any = yield call(fetchPaymentSlip, paymentApprovalId);
		yield put({ type: CommercialPaymentActionType.STORE_PAYMENT_SLIPS, payload: data });
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	} finally {
		finalCB();
	}
}

export function* approvePaymentEffect(action: AnyAction): CommercialPaymentSagaEffect {
	const { errorCB, paymentApprovalId, paymentValue, successCB, finalCB } = action.payload;

	try {
		const { data }: any = yield call(approvePayment, paymentValue, paymentApprovalId);
		successCB();
	} catch (error: unknown) {
		errorCB(error?.message);
	} finally {
		finalCB();
	}
}

export function* fetchAllPaymentsSaga(): CommercialPaymentSagaForEffect {
	yield takeLatest(CommercialPaymentActionType.FETCH_ALL_PAYMENTS, fetchAllCommercialPayments);
}

export function* fetchProjectAutocompleteSaga(): CommercialPaymentSagaForEffect {
	yield takeLatest(CommercialPaymentActionType.FETCH_PROJECT_AUTOCOMPLETE, fetchProjectAutocompleteEffect);
}

export function* fetchCustomerSlipSaga(): CommercialPaymentSagaForEffect {
	yield takeLatest(CommercialPaymentActionType.FETCH_CUSTOMER_SLIPS, fetchCustomerSlipsEffect);
}

export function* fetchPaymentSlipSaga(): CommercialPaymentSagaForEffect {
	yield takeLatest(CommercialPaymentActionType.FETCH_PAYMENT_SLIPS, fetchPaymentSlipsEffect);
}

export function* ApprovePaymentSaga(): CommercialPaymentSagaForEffect {
	yield takeLatest(CommercialPaymentActionType.APPROVE_PAYMENT, approvePaymentEffect);
}

export function* createGstInvoiceEffect(action: AnyAction): CommercialPaymentSagaEffect {
	const { errorCB, paymentApprovalId, gstInvoiceData, successCB, finalCB } = action.payload;

	try {
		const { data }: any = yield call(createGstInvoice, paymentApprovalId, handleEmptyNullValues(gstInvoiceData));
		yield put({ type: CommercialPaymentActionType.STORE_GST_INVOICE, payload: data });
		successCB(data);
	} catch (error: unknown) {
		yield put({
			type: CommercialPaymentActionType.ERROR_GST_INVOICE,
			payload: {
				error
			}
		});
		errorCB(error?.message);
	} finally {
		finalCB();
	}
}

export function* fetchGstInvoiceEffect(action: AnyAction): CommercialPaymentSagaEffect {
	const { errorCB, paymentApprovalId, successCB } = action.payload;

	try {
		const { data }: any = yield call(fetchGstInvoice, paymentApprovalId);
		yield put({ type: CommercialPaymentActionType.STORE_GST_INVOICE, payload: data });
		successCB(data);
	} catch (error: unknown) {
		yield put({
			type: CommercialPaymentActionType.ERROR_GST_INVOICE,
			payload: {
				error
			}
		});
		errorCB(error?.message);
	}
}

export function* createGstInvoiceSaga(): CommercialPaymentSagaForEffect {
	yield takeLatest(CommercialPaymentActionType.CREATE_GST_INVOICE, createGstInvoiceEffect);
}

export function* fetchGstInvoiceSaga(): CommercialPaymentSagaForEffect {
	yield takeLatest(CommercialPaymentActionType.FETCH_GST_INVOICE, fetchGstInvoiceEffect);
}
